
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CategoryGroup } from '@/utils/estimateExportUtils';
import { formatCurrency } from '@/lib/utils';

interface EstimateSummaryTableProps {
  categorizedLineItems: CategoryGroup[];
  estimate: {
    tax_percentage: number;
    tax_inclusive?: boolean;
  };
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  isVatRegistered?: boolean;
  companyVatRate?: number;
  discountAmount?: number;
  discountPercentage?: number;
}

const EstimateSummaryTable: React.FC<EstimateSummaryTableProps> = ({ 
  categorizedLineItems, 
  estimate, 
  subtotal,
  taxAmount, 
  totalAmount,
  isVatRegistered,
  companyVatRate = 15,
  discountAmount,
  discountPercentage
}) => {
  // Helper function to get display name for categories in the totals table
  const getCategoryDisplayName = (categoryName: string) => {
    switch (categoryName) {
      case 'Labour':
        return 'Labour'; // Show as "Labour" in totals table
      case 'Materials':
        return 'Materials';
      case 'Other':
        return 'Other';
      case 'All-Inclusive Cost':
        return 'All-Inclusive Cost';
      default:
        return categoryName;
    }
  };

  // Show VAT line if company is VAT registered and has VAT rate > 0
  const showVat = isVatRegistered && companyVatRate > 0;
  
  return (
    <div className="mb-8">
      <Table className="w-full estimate-table">
        <TableHeader>
          <TableRow>
            <TableHead className="text-left border-table-cell" style={{ fontSize: '10px', fontWeight: 'bold', lineHeight: '1.15', width: '60%' }}>Item</TableHead>
            <TableHead className="text-right whitespace-nowrap border-table-cell" style={{ fontSize: '10px', fontWeight: 'bold', lineHeight: '1.15', width: '40%' }}>Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {categorizedLineItems.map((category) => (
            <TableRow key={category.categoryName}>
              <TableCell className="text-left border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', width: '60%' }}>
                {getCategoryDisplayName(category.categoryName)}
              </TableCell>
              <TableCell className="text-right whitespace-nowrap border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', width: '40%' }}>
                {formatCurrency(category.categoryTotal)}
              </TableCell>
            </TableRow>
          ))}
          {/* Subtotal Row - only show if VAT registered OR if there's a discount */}
          {(showVat || ((discountAmount ?? 0) > 0)) && (
            <TableRow>
              <TableCell className="text-left font-semibold border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', fontWeight: 'bold' }}>Subtotal</TableCell>
              <TableCell className="text-right font-semibold whitespace-nowrap border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', fontWeight: 'bold' }}>
                {formatCurrency(subtotal)}
              </TableCell>
            </TableRow>
          )}
          {/* Discount Row - if applicable */}
          {(discountAmount ?? 0) > 0 && (
            <TableRow>
              <TableCell className="text-left border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15' }}>
                Discount (-{discountPercentage}%)
              </TableCell>
              <TableCell className="text-right whitespace-nowrap border-table-cell text-primary" style={{ fontSize: '10px', lineHeight: '1.15' }}>
                -{formatCurrency(discountAmount)}
              </TableCell>
            </TableRow>
          )}
          {/* VAT Row */}
          {showVat && (
            <TableRow>
              <TableCell className="text-left border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15' }}>VAT ({companyVatRate}%)</TableCell>
              <TableCell className="text-right whitespace-nowrap border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15' }}>
                {formatCurrency(taxAmount)}
              </TableCell>
            </TableRow>
          )}
          {/* Total Row */}
          <TableRow>
            <TableCell className="text-left font-bold border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', fontWeight: 'bold' }}>Total</TableCell>
            <TableCell className="text-right font-bold whitespace-nowrap border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', fontWeight: 'bold' }}>
              {formatCurrency(totalAmount)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};

export default EstimateSummaryTable;
